import { Commit } from 'vuex';
import {
	CalculatedEnergy,
	RealEnergy,
	RealEnergyEdit,
	RealEnergyMonthly,
	RealEnergyYear,
} from '@/interfaces/energy';
import Vue from 'vue';
import { Zone } from '@/interfaces/lighting';

interface EnergyState {
	calculatedEnergies: {
		[id: number]: CalculatedEnergy;
	};
	realEnergies: RealEnergyYear;
}

export default {
	namespaced: true,
	state: {
		calculatedEnergies: {},
		realEnergies: {},
	} as EnergyState,
	getters: {
		getCalculatedEnergies: (state: EnergyState): CalculatedEnergy[] => {
			return Object.values(state.calculatedEnergies);
		},

		getCalculatedEnergy:
			(state: EnergyState) =>
			(id: number): CalculatedEnergy | undefined => {
				return state.calculatedEnergies[id];
			},

		getRealEnergies: (state: EnergyState): RealEnergyMonthly[] => {
			return Object.values(state.realEnergies);
		},

		getRealEnergyMonth:
			(state: EnergyState) =>
			(month: string): RealEnergy[] => {
				return Object.values(state.realEnergies[month]);
			},
	},
	mutations: {
		SET_CALCULATED_ENERGY(
			state: EnergyState,
			{ calculatedEnergies }: { calculatedEnergies: CalculatedEnergy[] }
		): void {
			const ret: { [id: number]: CalculatedEnergy } = {};
			const templates = calculatedEnergies.reduce((obj, calculatedEnergy) => {
				obj[calculatedEnergy.zone_id] = calculatedEnergy;
				return obj;
			}, ret);

			state.calculatedEnergies = {
				...state.calculatedEnergies,
				...templates,
			};
		},

		SET_REAL_ENERGY(
			state: EnergyState,
			{ realEnergies, month }: { realEnergies: RealEnergy[]; month: string }
		): void {
			const ret: { [id: number]: RealEnergy } = {};
			const energyYear: RealEnergyYear = { ...state.realEnergies };
			const templates = realEnergies.reduce((obj, realEnergy) => {
				obj[realEnergy.zone_id] = realEnergy;
				return obj;
			}, ret);

			energyYear[month] = { ...energyYear[month], ...templates };

			state.realEnergies = energyYear;
		},

		CLEAN_CALCULATED_ENERGY(state: EnergyState): void {
			state.calculatedEnergies = {};
		},

		CLEAN_REAL_ENERGY(state: EnergyState): void {
			state.realEnergies = {};
		},
	},
	actions: {
		async fetchRealEnergy(
			{ commit }: { commit: Commit },
			{ payload, zones }: { payload: RealEnergyEdit[]; zones: Zone[] }
		): Promise<void> {
			const queryParams = new URLSearchParams();
			for (const devices of payload) {
				const periods = `${String(devices['start_date'])},${String(devices['end_date'])},${String(
					devices['month']
				)}`;
				queryParams.append('periods', periods);
			}

			for (const zone of zones) {
				queryParams.append('zones', String(zone['id']));
			}

			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/lighting/energy/real/`, {
				params: queryParams,
			});

			if (response && response.status === 200) {
				for (const response_data of response.data.data) {
					commit('SET_REAL_ENERGY', {
						realEnergies: [response_data.energy],
						month: response_data.month,
					});
				}
			}
		},

		async fetchCalculatedEnergy(
			{ commit }: { commit: Commit },
			{ zones }: { zones: Zone[] }
		): Promise<void> {
			const queryParams = new URLSearchParams();
			for (const zone of zones) {
				queryParams.append('zones', String(zone['id']));
			}

			const response = await Vue.axios.get(
				`${process.env.VUE_APP_API_URL}/lighting/energy/calculated/`,
				{
					params: queryParams,
				}
			);

			if (response && response.status === 200) {
				for (const response_data of response.data.data) {
					commit('SET_CALCULATED_ENERGY', { calculatedEnergies: [response_data] });
				}
			}
		},

		cleanAllEnergy({ commit }: { commit: Commit }): void {
			commit('CLEAN_CALCULATED_ENERGY');
			commit('CLEAN_REAL_ENERGY');
		},
	},
};
